// src/components/RegistrationModal.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { addToLapostaList } from '../utils/laposta';
import SchoolSearchField from './SchoolSearchField';

const STRAPI_URL = process.env.REACT_APP_STRAPI_URL;

// kleine helper om stap 1 velden visueel te verbergen maar wél in de DOM te houden
const visuallyHidden = {
  position: 'absolute',
  left: '-9999px',
  width: '1px',
  height: '1px',
  overflow: 'hidden',
  opacity: 0,
  pointerEvents: 'none',
};

function RegistrationModal({ onClose, onRegisterSuccess }) {
  const [step, setStep] = useState(1);

  // Stap 1
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [educationRole, setEducationRole] = useState('');
  const [educationRoleOther, setEducationRoleOther] = useState('');
  const [educationSector, setEducationSector] = useState('');
  const [isStudent, setIsStudent] = useState(false);

  // Stap 2
  const [schoolName, setSchoolName] = useState('');
  const [city, setCity] = useState('');
  const [brin, setBrin] = useState('');
  const [opleiding, setOpleiding] = useState('');

  const handleSchoolChange = (patch) => {
    if ('brin' in patch) setBrin(patch.brin);
    if ('schoolName' in patch) setSchoolName(patch.schoolName);
    if ('city' in patch) setCity(patch.city);
    if ('opleiding' in patch) setOpleiding(patch.opleiding);
  };

  const [error, setError] = useState('');
  const [showModalContent, setShowModalContent] = useState(false);
  const [registrationDone, setRegistrationDone] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setShowModalContent(true), 50);
    return () => clearTimeout(timer);
  }, []);

  const goFromStep1ToStep2 = () => {
    setError('');
    if (!fullName || !email || !password || !passwordConfirm || !educationRole || !educationSector) {
      setError('Vul alle verplichte velden in.');
      return;
    }
    if (password !== passwordConfirm) {
      setError('De wachtwoorden komen niet overeen.');
      return;
    }
    if (educationRole === 'anders' && !educationRoleOther) {
      setError('Vul je rol in bij het "anders" veld.');
      return;
    }
    setStep(2);
  };

  const finalSubmit = async () => {
    setError('');

    const registrationData = {
      username: email,
      email,
      password,
      fullName,
      educationRole,
      educationRoleOther: educationRole === 'anders' ? educationRoleOther : '',
      educationSector,
      isStudent,
      schoolName,
      city,
      brin,
      opleiding,
    };

    try {
      const response = await axios.post(
        `${STRAPI_URL}/api/auth/local/register`,
        registrationData
      );

      const jwt = response?.data?.jwt;

      // Voeg gebruiker toe aan Laposta mailinglijst
      // Dit gebeurt asynchroon en mag registratie niet blokkeren
      addToLapostaList({
        email,
        fullName,
        educationRole,
        educationRoleOther,
        educationSector,
        schoolName,
        city,
      });

      if (jwt) {
        // Email confirmation UIT → direct ingelogd
        localStorage.setItem('jwt', jwt);
        onRegisterSuccess && onRegisterSuccess();
      } else {
        // Email confirmation AAN → toon bevestiging
        setRegistrationDone(true);
      }
    } catch (err) {
      const errorMessage =
        err.response?.data?.error?.message ||
        'Registreren lukte niet. Probeer het opnieuw.';
      setError(errorMessage);
      setStep(1);
    }
  };

  // Eén formulier dat beide stappen omvat:
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (registrationDone) return;

    if (step === 1) {
      // Enter op stap 1 → door naar stap 2
      goFromStep1ToStep2();
    } else {
      // stap 2 → registreren
      await finalSubmit();
    }
  };

  return (
    <div className={`modal-backdrop ${showModalContent ? 'show' : ''}`}>
      <div
        className={`modal-content ${showModalContent ? 'show' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2>
            {registrationDone
              ? 'Registratie voltooid'
              : step === 1
              ? 'Registreren'
              : 'Bijna klaar...'}
          </h2>
          <button className="modal-close-btn" onClick={onClose}>×</button>
        </div>

        {registrationDone ? (
          <div className="info-message" style={{ textAlign: 'center' }}>
            <p><strong>Je account is klaar.</strong></p>
            <p>Controleer je mailbox om je e-mailadres te bevestigen voordat je kunt inloggen.</p>
          </div>
        ) : (
          <form id="registration-form" onSubmit={handleFormSubmit} autoComplete="on">
            {/* ===== Stap 1: verplichte velden (blijven in DOM) ===== */}
            <div style={step === 1 ? {} : visuallyHidden} aria-hidden={step !== 1}>
              <div className="form-group">
                <label htmlFor="reg-fullname">Volledige naam*</label>
                <input
                  id="reg-fullname"
                  name="name"
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  autoComplete="name"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="reg-email">E-mailadres*</label>
                <input
                  id="reg-email"
                  name="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email" /* of "username", beide werken; MDN + WHATWG */
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="reg-password">Wachtwoord*</label>
                <div style={{ position: 'relative' }}>
                  <input
                    id="reg-password"
                    name="new-password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="new-password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    style={{ position: 'absolute', right: '0.5rem', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: '0.25rem', color: '#888', fontSize: '0.8rem' }}
                    tabIndex={-1}
                    aria-label={showPassword ? 'Wachtwoord verbergen' : 'Wachtwoord tonen'}
                  >
                    {showPassword ? '🙈' : '👁️'}
                  </button>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="reg-password-confirm">Wachtwoord bevestigen*</label>
                <div style={{ position: 'relative' }}>
                  <input
                    id="reg-password-confirm"
                    name="new-password-confirm"
                    type={showPassword ? 'text' : 'password'}
                    value={passwordConfirm}
                    onChange={(e) => setPasswordConfirm(e.target.value)}
                    autoComplete="new-password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    style={{ position: 'absolute', right: '0.5rem', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: '0.25rem', color: '#888', fontSize: '0.8rem' }}
                    tabIndex={-1}
                    aria-label={showPassword ? 'Wachtwoord verbergen' : 'Wachtwoord tonen'}
                  >
                    {showPassword ? '🙈' : '👁️'}
                  </button>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="reg-role">Rol in het onderwijs*</label>
                <select
                  id="reg-role"
                  name="educationRole"
                  value={educationRole}
                  onChange={(e) => setEducationRole(e.target.value)}
                  required
                >
                  <option value="" disabled>Kies een rol...</option>
                  <option value="leraar">Leraar</option>
                  <option value="schoolleider">Schoolleider</option>
                  <option value="bestuurder">Bestuurder</option>
                  <option value="anders">Anders</option>
                </select>
              </div>

              {educationRole === 'anders' && (
                <div className="form-group">
                  <label htmlFor="reg-role-other">Anders, namelijk*</label>
                  <input
                    id="reg-role-other"
                    name="educationRoleOther"
                    type="text"
                    value={educationRoleOther}
                    onChange={(e) => setEducationRoleOther(e.target.value)}
                    required
                  />
                </div>
              )}

              <div className="form-group">
                <label htmlFor="reg-sector">Onderwijssector*</label>
                <select
                  id="reg-sector"
                  name="educationSector"
                  value={educationSector}
                  onChange={(e) => setEducationSector(e.target.value)}
                  required
                >
                  <option value="" disabled>Kies een sector...</option>
                  <option value="PO">PO</option>
                  <option value="VO">VO</option>
                  <option value="MBO">MBO</option>
                  <option value="HBO">HBO</option>
                  <option value="S(VO)">S(VO)</option>
                </select>
              </div>

              <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <input
                  id="reg-student"
                  name="isStudent"
                  type="checkbox"
                  checked={isStudent}
                  onChange={(e) => setIsStudent(e.target.checked)}
                  style={{ width: 'auto' }}
                />
                <label htmlFor="reg-student" style={{ marginBottom: 0 }}>Ik ben een student</label>
              </div>

              {error && <p className="error-message">{error}</p>}

              {/* Enter of klik → naar stap 2, géén submit naar server */}
              <button type="submit" className="btn-primary">Volgende</button>
            </div>

            {/* ===== Stap 2: optioneel (in hetzelfde formulier) ===== */}
            <div style={step === 2 ? {} : visuallyHidden} aria-hidden={step !== 2}>
              <div className="info-message" style={{ textAlign: 'left' }}>
                <strong>Nog één stap en je haalt alles uit Doorloopjes.nl.</strong>
                <p style={{ marginTop: '0.5rem', marginBottom: 0 }}>
                  Vul je school en plaats in. Dan krijg je tips die bij jou passen.
                </p>
              </div>

              <SchoolSearchField
                brin={brin}
                schoolName={schoolName}
                city={city}
                opleiding={opleiding}
                educationSector={educationSector}
                onChange={handleSchoolChange}
                idPrefix="reg"
              />

              {error && <p className="error-message">{error}</p>}

              <div style={{ display: 'flex', gap: '1rem' }}>
                {/* Beide knoppen zijn submit → echte form submit-event voor passwordmanagers */}
                <button type="submit" className="btn-secondary" style={{ flex: 1 }}>
                  Overslaan
                </button>
                <button type="submit" className="btn-primary" style={{ flex: 1 }}>
                  Registratie voltooien
                </button>
              </div>
              <p className="field-hint" style={{ marginTop: '0.75rem', marginBottom: 0 }}>
                Je krijgt ook onze nieuwsbrief. Afmelden kan altijd, met de link onderaan de mail.
              </p>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default RegistrationModal;
